import { useCallback, useState } from "react";

import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import {
    createWebLensUpload as createWebLensUploadMutation,
    previewWebLensOnDevice as previewWebLensOnDeviceMutation,
} from "../api/mutations";
import {
    type CreateWebLensUploadMutation,
    type CreateWebLensUploadMutationVariables,
} from "../api/mutations/createWebLensUpload.gql-generated";
import {
    type PreviewWebLensOnDeviceMutation,
    type PreviewWebLensOnDeviceMutationVariables,
} from "../api/mutations/previewWebLensOnDevice.gql-generated";
import { WebLensUploadError, putToSignedUrl } from "../api/putToSignedUrl";
import { messages } from "../components/messages.generated";
import { type SubmissionCheck, type WebLensPushResult } from "../model";
import { describeWebLensCheckLabel } from "../utils/describeWebLensCheckLabel";
import { describeWebLensRejection } from "../utils/describeWebLensRejection";
import { type WebLensLogoMeasurement } from "../utils/measureWebLensLogo";
import {
    captureWebLensException,
    failedCheckReason,
    startWebLensAttempt,
    uploadFailureReason,
} from "../utils/webLensSentry";
import { preChecksPassed, runPushClientPreChecks } from "../validation/preChecks";

interface PreviewWebLensOnDeviceState {
    checks: SubmissionCheck[];
    result: WebLensPushResult | null;
    pushing: boolean;
    push: (
        file: File,
        logo: File | null,
        name: string,
        logoMeasurement: WebLensLogoMeasurement | null,
    ) => Promise<void>;
    reset: () => void;
}

/**
 * Push-to-device preview for an UNSUBMITTED web lens — the same ticket + signed-URL upload as
 * submission, then previewWebLensOnDevice instead of submitWebLens. The zip→.lens conversion runs
 * INSIDE that call (the light push-to-device pipeline), so unlike submission a rejected archive
 * fails right here, with the conversion's creator-facing reason on its check row. On success the
 * lens is already on its way to the creator's Snapchat; there is no lens record and nothing to
 * navigate to, so `result` is the page's terminal success signal.
 */
export const usePreviewWebLensOnDevice = (): PreviewWebLensOnDeviceState => {
    const intl = useIntl();
    const [checks, setChecks] = useState<SubmissionCheck[]>([]);
    const [result, setResult] = useState<WebLensPushResult | null>(null);
    const [uploading, setUploading] = useState(false);
    const [executeCreateUpload] = useMutation<CreateWebLensUploadMutation, CreateWebLensUploadMutationVariables>(
        createWebLensUploadMutation,
    );
    const [executePush, { loading: pushLoading }] = useMutation<
        PreviewWebLensOnDeviceMutation,
        PreviewWebLensOnDeviceMutationVariables
    >(previewWebLensOnDeviceMutation);

    const { sendBlizzardEvent } = useBlizzardEvents();
    const push = useCallback(
        async (
            file: File,
            logo: File | null,
            name: string,
            /** Page-level dimension measurement; the row must not vanish when push() takes the rail. */
            logoMeasurement: WebLensLogoMeasurement | null,
        ) => {
            setResult(null);
            // Attempt-owned instrumentation — see startWebLensAttempt for the outcome contract.
            const { trace: flowTrace, sendOutcome: sendOutcomeEvent } = startWebLensAttempt("push", sendBlizzardEvent);

            // Raw GraphQL/transport errors are never shown: they go to Sentry, and the creator sees
            // a localized fallback. Server-side validation rows (payload.validation) are different —
            // those details are creator-facing copy owned by the BFF and render verbatim, except
            // structured rejections, which describeWebLensRejection localizes.
            const describeUploadFailure = (error: unknown): string => {
                if (error instanceof WebLensUploadError) {
                    if (error.kind === "file_changed") {
                        return intl.formatMessage(messages.uploadFailedFileChanged);
                    }
                    if (error.kind === "stalled") {
                        return intl.formatMessage(messages.uploadFailedStalled);
                    }
                    if (error.kind === "http") {
                        return intl.formatMessage(messages.uploadFailedHttp, { status: error.status });
                    }
                    return intl.formatMessage(messages.uploadFailedNetwork);
                }
                captureWebLensException(error, "upload");
                return intl.formatMessage(messages.genericFailure);
            };

            const uploadCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "direct-upload",
                label: intl.formatMessage(messages.uploadCheckLabel),
                state,
                detail,
            });

            // Unlike submission, the conversion runs inside this call — the push row is the whole
            // pipeline's verdict, and its failure detail is the conversion's own reason.
            const pushCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "push",
                label: intl.formatMessage(messages.pushCheckLabel),
                state,
                detail,
            });

            const preChecks = runPushClientPreChecks(intl, file, logo, logoMeasurement);
            if (!preChecksPassed(preChecks)) {
                setChecks(preChecks);
                sendOutcomeEvent("web_lens_push_failed_precheck", failedCheckReason(preChecks));
                return;
            }

            // Bytes go browser → GCS via signed URLs — the only upload path. The per-kind size
            // caps are bound into the URL signatures, so GCS itself refuses an oversized upload.
            setUploading(true);
            const endUploadStage = flowTrace.startStage("upload");
            let directUpload: { packageRef: string; logoUploaded: boolean };
            try {
                setChecks([...preChecks, uploadCheck("running")]);
                const ticketResponse = await executeCreateUpload();
                const ticket = ticketResponse.data?.createWebLensUpload;
                if (!ticket) {
                    captureWebLensException(
                        ticketResponse.errors?.[0] ?? new Error("createWebLensUpload returned no ticket"),
                        "upload",
                    );
                    setChecks([...preChecks, uploadCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_push_failed_upload", "no_ticket");
                    return;
                }
                // The caps come from the ticket — the exact values bound into the URL signatures —
                // so the header can never drift from what the BFF signed.
                await putToSignedUrl(ticket.packageUploadUrl, file, ticket.packageMaxBytes, (fraction) => {
                    setChecks([...preChecks, uploadCheck("running", `${Math.round(fraction * 100)}%`)]);
                });
                if (logo) {
                    await putToSignedUrl(ticket.logoUploadUrl, logo, ticket.logoMaxBytes);
                }
                directUpload = { packageRef: ticket.uploadRef, logoUploaded: Boolean(logo) };
            } catch (error) {
                setChecks([...preChecks, uploadCheck("fail", describeUploadFailure(error))]);
                sendOutcomeEvent("web_lens_push_failed_upload", uploadFailureReason(error));
                return;
            } finally {
                endUploadStage();
                setUploading(false);
            }

            const baseChecks: SubmissionCheck[] = [...preChecks, uploadCheck("pass")];
            setChecks([...baseChecks, pushCheck("running")]);

            flowTrace.startStage("mutation");
            try {
                const trimmedName = name.trim();
                const response = await executePush({
                    variables: {
                        input: {
                            packageRef: directUpload.packageRef,
                            logoUploaded: directUpload.logoUploaded,
                            // The name is optional — blank means the platform's default name, so
                            // it is omitted rather than sent empty.
                            ...(trimmedName ? { name: trimmedName } : {}),
                        },
                    },
                });

                const payload = response.data?.previewWebLensOnDevice;
                if (!payload) {
                    captureWebLensException(
                        response.errors?.[0] ?? new Error("previewWebLensOnDevice returned an empty response"),
                        "push",
                    );
                    setChecks([...baseChecks, pushCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_push_failed_push", "empty_payload");
                    return;
                }

                const serverChecks: SubmissionCheck[] = payload.validation.map((check) => {
                    // Localize a structured rejection; anything else keeps the BFF's verbatim detail.
                    const described =
                        !check.passed && check.rejection ? describeWebLensRejection(intl, check.rejection) : undefined;
                    return {
                        id: check.name,
                        label: describeWebLensCheckLabel(intl, check.name),
                        state: check.passed ? "pass" : "fail",
                        detail: described?.detail ?? check.detail ?? undefined,
                        issues: described?.issues,
                    };
                });
                setChecks([...baseChecks, ...serverChecks]);

                // previewLensId can be legitimately null on success (proto int64 default), so the
                // rows are the success signal — for the outcome event AND the success panel.
                const pushSucceeded = payload.validation.every((check) => check.passed);
                sendOutcomeEvent(
                    pushSucceeded ? "web_lens_push_success" : "web_lens_push_failed_push",
                    pushSucceeded ? undefined : failedCheckReason(serverChecks),
                );
                if (pushSucceeded) {
                    setResult({ previewLensId: payload.previewLensId ?? null });
                }
            } catch (error) {
                captureWebLensException(error, "push");
                setChecks([...baseChecks, pushCheck("fail", intl.formatMessage(messages.genericFailure))]);
                sendOutcomeEvent("web_lens_push_failed_push", "transport");
            }
        },
        [executeCreateUpload, executePush, intl, sendBlizzardEvent],
    );

    const reset = useCallback(() => {
        setChecks([]);
        setResult(null);
    }, []);

    return { checks, result, pushing: pushLoading || uploading, push, reset };
};
